import { ButtonBase, makeStyles, Typography, useMediaQuery, Box } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useFormikContext } from "formik";
import moment from "moment";
import "moment-timezone";
import React, { useState } from "react";

import useCart from "hooks/useCart";
import { useQuery } from "react-query";
import { FunnelCardContainer } from "ui/funnelCardContainer/FunnelCardContainer";
import ArrowLeft from "../../../assets/arrowLeftCalendar.svg";
import ArrowRight from "../../../assets/arrowRightCalendar.svg";
import HelpTextCard from "components/design-system/molecules/HelpTextCard";
import SlotChoices from "./SlotChoices";
import { calendarSkeleton, formatData, getMaxTimeslots } from "./utils";

export default function FunnelShippingCalendar() {
  const [cart] = useCart();
  const theme = useTheme();
  const formik = useFormikContext();

  const isTablet = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const columns = isMobile ? [0] : isTablet ? [0, 0] : [0, 0, 0];
  const [slider, setSlider] = useState(0);
  const [previousSliderMove, setPreviousSliderMove] = useState(columns.length);
  const [limitDate] = useState(moment().add(5, "weeks").format("YYYY-MM-DD"));
  const today = moment().format("YYYY-MM-DD");
  const classes = useStyle({ isTablet });

  let start_date = moment().add(slider, "days").format("YYYY-MM-DD");
  const { data: availableTimeslots, error } = useQuery(
    [
      `timeslot/shipping-timeslots`,
      {
        postal_code: cart.shipping.address.zipcode,
        start_date: start_date,
        end_date: moment()
          .add(slider + columns.length - 1, "days")
          .format("YYYY-MM-DD"),
        shipping_type: cart.shipping.recoverOldDevice ? "delivery_and_collection" : "delivery",
        delivery_type: cart.shipping.delivery_option_type,
        product: cart.selection.map((selection) => selection.product.id),
        address: cart.shipping.address.address_id,
      },
    ],
    { retry: false }
  );
  let formattedTimeslots = [];
  if (availableTimeslots) {
    const formattedData = availableTimeslots.reduce(formatData, {});
    const maxTimeslots = getMaxTimeslots(formattedData);
    let dates = calendarSkeleton(columns.length, start_date);
    formattedTimeslots = dates.map((date) => {
      const timeslots = formattedData[date.day] || [];
      let sortedTimeslots = timeslots;
      if (timeslots.length > 0) {
        sortedTimeslots = [...timeslots, ...Array.from({ length: maxTimeslots - timeslots.length })].sort(
          (timeslotA, timeslotB) => {
            if (timeslotA.end < timeslotB.end) {
              return -1;
            }
            return 1;
          }
        );
      }
      return { ...date, timeslots: sortedTimeslots };
    });
    let noTimeslotsDisplayed =
      formattedTimeslots.filter((formattedTimeslot) => formattedTimeslot.timeslots.length === 0).length ===
      formattedTimeslots.length;
    // Go to next day(s) if no timeslot is displayed and between limit dates
    if (noTimeslotsDisplayed && limitDate > start_date && start_date > today) {
      setSlider((prev) => prev + previousSliderMove);
    }
    // Go to next day(s) in future if no timeslot is displayed on today's date
    if (noTimeslotsDisplayed && start_date === today) {
      setPreviousSliderMove(columns.length);
      setSlider((prev) => prev + columns.length);
    }
  }

  const noAvailableTimeslots = (
    <FunnelCardContainer>
      <div className={classes.container}>
        <Typography color="secondary" variant="h2" className={classes.title}>
          Oups, il semblerait que tous nos créneaux soient occupés.
        </Typography>
        <Typography>
          Malheureusement, aucun créneau ne correspond à votre addresse dans nos prochaines disponibilités.
        </Typography>
      </div>
    </FunnelCardContainer>
  );

  const noZone = (
    <div className={classes.container}>
      <Typography color="secondary" variant="h2" className={classes.title}>
        Toutes nos excuses, nous ne livrons pas encore chez vous !
      </Typography>
      <Typography>
        Afin de pouvoir assurer un service client de qualité, nous ne livrons de produits que dans les zones où nos
        techniciens peuvent intervenir. Nous travaillons à étendre ces zones d&rsquo;intervention rapidement !
      </Typography>
    </div>
  );

  return (
    <>
      <Box mb={4}>
        <Typography color="secondary" variant="h2">
          Choisissez votre créneau de livraison
        </Typography>
      </Box>
      {cart.selection.length > 1 && (
        <HelpTextCard mb={3}>Les {cart.selection.length} appareils vous seront livrés en même temps</HelpTextCard>
      )}
      <div className={classes.container}>
        <FunnelCardContainer disablePadding>
          <div className={classes.header}>
            {!error && slider > 0 ? (
              <ButtonBase
                disableRipple
                onClick={() => {
                  setSlider((prev) => prev - columns.length);
                  setPreviousSliderMove(-columns.length);
                }}
              >
                <ArrowLeft />
              </ButtonBase>
            ) : (
              <div />
            )}
            <div className={classes.headerBox}>
              <div className={`${classes.dayContainer}`}>
                {formattedTimeslots.map((item, index) => (
                  <div key={`${index}`} className={classes.day}>
                    <Typography className={classes.date}>{item.displayDate}</Typography>
                  </div>
                ))}
              </div>
            </div>
            {!error && (
              <ButtonBase
                disableRipple
                onClick={() => {
                  setSlider((prev) => prev + columns.length);
                  setPreviousSliderMove(+columns.length);
                }}
              >
                <ArrowRight />
              </ButtonBase>
            )}
          </div>

          <div className={classes.choicesContainer}>
            {limitDate < start_date ? (
              noAvailableTimeslots
            ) : error ? (
              noZone
            ) : (
              <div className={`${classes.slotsContainer}`}>
                {formattedTimeslots.map((item, index) => (
                  <SlotChoices key={`${index}`} timeslots={item.timeslots} />
                ))}
              </div>
            )}
          </div>
        </FunnelCardContainer>
      </div>
      <Typography color="error" className={classes.error}>
        {formik.submitCount > 0 && formik.errors.date}
      </Typography>
    </>
  );
}

const useStyle = makeStyles((theme) => ({
  error: {
    fontSize: 16,
    marginTop: theme.spacing(),
  },
  headerBox: {
    position: "relative",
    maxWidth: "619px",
    overflow: "hidden",
    height: 23,
    flexBasis: "auto",
    flexGrow: 1,
    flexShrink: 1,
    [theme.breakpoints.down("md")]: {
      maxWidth: "unset",
    },
  },
  choicesContainer: {
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    overflow: "hidden",
    display: "block",
  },
  spring: {
    display: "flex",
  },
  day: {
    flex: 1,
    justifyContent: "center",
    display: "flex",
  },
  dayContainer: {
    display: "flex",
    justifyContent: (props) => (props.isTablet ? "space-around" : "space-between"),
    minWidth: "100%",
  },
  slotsContainer: {
    display: "flex",
    minWidth: "100%",
    justifyContent: "space-between",
  },
  header: {
    borderTopRightRadius: 10,
    borderTopLeftRadius: 10,
    display: "flex",
    backgroundColor: theme.palette.secondary.main,
    padding: theme.spacing(2),
    columnGap: theme.spacing(),
    justifyItems: "center",
  },
  date: {
    color: theme.palette.secondary.contrastText,
    fontWeight: 500,
  },
  container: {
    background: theme.palette.grey200.main,
    borderRadius: 10,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
      maxWidth: "704px",
    },
  },
}));
